import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, can } from "../context/AuthContext";
import { Plus, RotateCcw, Eye, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { fmt, today } from "../utils";
import { useLang } from "../i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import DocumentPreviewDialog from "../components/print/DocumentPreviewDialog";

export default function Returns() {
  const { user } = useAuth();
  const submitting = useRef(false);
  const requestKey = useRef(null);
  const [tab, setTab] = useState("sale");
  const [rows, setRows] = useState([]);
  const [bills, setBills] = useState([]);
  const [open, setOpen] = useState(false);
  const [billId, setBillId] = useState("");
  const [bill, setBill] = useState(null);
  const [qtys, setQtys] = useState({});
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(today());
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const { t } = useLang();

  const load = async (signal) => {
    const [notes, invoices] = await Promise.all([
      api.get("/returns", { params: { kind: tab }, signal }),
      api.get(tab === "sale" ? "/sales" : "/purchases", { signal }),
    ]);
    if (!signal?.aborted) { setRows(notes.data); setBills(invoices.data); }
  };
  useEffect(() => {
    const controller = new AbortController();
    setOpen(false); setBillId(""); setBill(null); setRows([]); setBills([]);
    load(controller.signal).catch((err) => { if (!controller.signal.aborted) toast.error(err.message); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (!billId) { setBill(null); return; }
    const controller = new AbortController();
    setBill(null); setQtys({});
    api.get(`/returns/availability/${tab}/${billId}`, { signal: controller.signal }).then((r) => {
      if (!controller.signal.aborted) setBill(r.data);
    }).catch((err) => { if (!controller.signal.aborted) toast.error(err.message); });
    return () => controller.abort();
  }, [billId, tab]);

  const submit = async () => {
    if (submitting.current) return;
    const lines = (bill?.items || [])
      .map((it, index) => ({ line_index: index, item_id: it.item_id, batch_ref: tab === "sale" ? it.batch_id : it.batch_no, qty: Number(qtys[index] || 0) }))
      .filter((l) => l.qty > 0);
    if (!lines.length) return toast.error("Kisi line mein return qty daalo");
    submitting.current = true;
    setSaving(true);
    try {
      const payload = { bill_id: billId, date, reason, lines };
      const fingerprint = JSON.stringify(payload);
      if (requestKey.current?.fingerprint !== fingerprint) requestKey.current = { fingerprint, key: crypto.randomUUID() };
      const ret = (await api.post(`/returns/${tab}`, payload, { headers: { "Idempotency-Key": requestKey.current.key } })).data;
      toast.success(`${ret.ret_no} saved — stock aur ledger adjust ho gaya`);
      setOpen(false); setBillId(""); setBill(null); setReason(""); await load();
    } catch (err) {
      toast.error(err.message);
      try { setBill((await api.get(`/returns/availability/${tab}/${billId}`)).data); } catch (refreshError) { toast.error(refreshError.message); }
    }
    submitting.current = false;
    setSaving(false);
  };

  return (
    <div data-testid="returns-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("returns")}</h1>
          <p className="sgp-page-subtitle">Sales Return (Credit Note) aur Purchase Return (Debit Note) — stock aur ledger dono auto-adjust hote hain.</p>
        </div>
        <button disabled={!can(user, tab === "sale" ? "invoice" : "purchase", "full")} data-testid="add-return-btn" onClick={() => setOpen(true)}
          className="sgp-primary-btn">
          <Plus className="h-4 w-4" /> {t("add")}
        </button>
      </div>

      <div className="flex gap-2">
        <button data-testid="returns-tab-sale" onClick={() => setTab("sale")}
          className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${tab === "sale" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-slate-600"}`}>
          Sales Return (Hospital se wapas — CN)
        </button>
        {can(user, "purchase", "view") && <button data-testid="returns-tab-purchase" onClick={() => setTab("purchase")}
          className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${tab === "purchase" ? "bg-rose-600 border-rose-600 text-white" : "border-slate-300 text-slate-600"}`}>
          Purchase Return (Company ko wapas — DN)
        </button>}
      </div>

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr>
              <th className="sgp-th">{tab === "sale" ? "CN No." : "DN No."}</th><th className="sgp-th">Date</th>
              <th className="sgp-th">Against Bill</th><th className="sgp-th">Party</th>
              <th className="sgp-th">Reason</th><th className="sgp-th text-right">Amount</th><th className="sgp-th">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="returns-table">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`return-row-${r.id}`} className="sgp-table-row">
                <td className="sgp-td font-mono text-xs font-semibold"><Link className="text-teal-700 underline" to={`/print/${r.kind === "sale" ? "credit_note" : "debit_note"}/${r.id}`}>{r.ret_no}</Link></td>
                <td className="sgp-td font-mono text-xs">{r.date}</td>
                <td className="sgp-td font-mono text-xs">{r.bill_no}</td>
                <td className="sgp-td">{r.party}</td>
                <td className="sgp-td text-xs">{r.reason || "-"}</td>
                <td className="sgp-td text-right font-mono font-semibold">{fmt(r.grand_total)}</td>
                <td className="sgp-td">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setPreview({ kind: r.kind === "sale" ? "credit_note" : "debit_note", id: r.id, label: `${r.kind === "sale" ? "Credit Note" : "Debit Note"} ${r.ret_no}` })} className="inline-flex items-center gap-1 text-blue-700 font-semibold"><Eye className="h-4 w-4" /> Preview</button>
                    <Link target="_blank" rel="noreferrer" to={`/print/${r.kind === "sale" ? "credit_note" : "debit_note"}/${r.id}`} className="inline-flex items-center gap-1 text-teal-700 font-semibold"><Printer className="h-4 w-4" /> Print</Link>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} className="sgp-td text-center py-10 text-slate-400">
                <RotateCcw className="h-6 w-6 mx-auto mb-2 text-slate-300" />Abhi koi {tab === "sale" ? "sales" : "purchase"} return nahi.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <DocumentPreviewDialog open={!!preview} onClose={() => setPreview(null)} kind={preview?.kind} id={preview?.id} label={preview?.label} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="return-form-modal" className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">New {tab === "sale" ? "Sales Return (CN)" : "Purchase Return (DN)"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="sgp-label">Original Bill *</label>
                <select data-testid="return-bill-select" className="sgp-input" value={billId} onChange={(e) => setBillId(e.target.value)}>
                  <option value="">— Select bill —</option>
                  {bills.map((b) => <option key={b.id} value={b.id}>{b.bill_no} · {tab === "sale" ? b.hospital_name : b.company_name} · {fmt(b.grand_total)}</option>)}
                </select></div>
              <div><label className="sgp-label">Return Date</label>
                <input data-testid="return-date-input" type="date" className="sgp-input font-mono" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            </div>
            {bill && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50">
                    <th className="px-3 py-2 text-left font-semibold text-slate-500">Item</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500">Batch</th>
                    <th className="px-3 py-2 text-right font-semibold text-slate-500">Remaining Qty</th>
                    <th className="px-3 py-2 text-right font-semibold text-slate-500 w-28">Return Qty</th>
                  </tr></thead>
                  <tbody>
                    {bill.items.map((it, index) => {
                      const key = index;
                      return (
                        <tr key={key} className="border-t border-slate-100">
                          <td className="px-3 py-2">{it.item_name}</td>
                          <td className="px-3 py-2 font-mono">{it.batch_no || "-"}</td>
                          <td className="px-3 py-2 text-right font-mono">{it.remaining_qty}</td>
                          <td className="px-3 py-2 text-right">
                            <input data-testid={`return-qty-${key}`} type="number" min="0" step="any" max={it.remaining_qty}
                              className="sgp-input font-mono w-24 text-right h-8"
                              value={qtys[key] || ""} onChange={(e) => setQtys({ ...qtys, [key]: e.target.value })} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div><label className="sgp-label">Reason (expiry / damage / rate difference)</label>
              <input data-testid="return-reason-input" className="sgp-input" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
            <button data-testid="return-save-btn" onClick={submit} disabled={saving}
              className="w-full py-2 text-sm font-semibold rounded-md bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50">
              {saving ? "Saving..." : "Save Return"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

